import { GlobalSearchTabPanelProps } from './GlobalSearchTabPanelTypes';
import { fetchUnifiedSearch } from '@/lib/api';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { styled } from '@mui/material/styles';
import {
  ComplianceDocument,
  FolderStructureResponse,
  UnifiedSearchResponse,
  WithFormatted,
} from '@/lib/types';
import CircularProgress from '@mui/material/CircularProgress';
import { FormattedMessage } from 'react-intl';
import ModeSwitch from '@/components/FileExplorer/ModeSwitch';
import { Mode } from '@/components/FileExplorer/FileExplorerHelpers';
import FileGrid from '@/components/FileExplorer/FileGrid';
import DownloadIcon from '@/assets/download.svg?react';
import { useDownload } from '@/hooks/useDownload';
import FileExplorer from '@/components/FileExplorer/FileExplorer';
import { parseSearchHitPath } from '@/pages/Resources/ResourcePageHelpers';
import Button from '@/components/Button/Button';
import { assetQueryKeys } from '@/lib/assetQueryKeys';
import { ga } from '@/lib/analytics';
import { useBasketContext } from '@/contexts/BasketContext';

const EMPTY_FOLDER_STRUCTURE: FolderStructureResponse = {
  folders: [
    {
      path: '',
      name: '',
      file_count: 0,
      total_file_count: 0,
      subfolders: [],
    },
  ],
  total_files: 0,
};

const ComplianceDocumentsSearchTabPanel = ({
  query,
  scrollContainerRef,
  onResultsLoaded,
  onLoadedFilesChange,
}: GlobalSearchTabPanelProps) => {
  const trimmedQuery = query.trim();
  const hasSufficientQuery = trimmedQuery.length >= 2;
  const [viewMode, setViewMode] = useState<Mode>('grid');
  const { idsFor, addMany, removeMany } = useBasketContext();
  const selected = useMemo(() => idsFor('compliance_documents'), [idsFor]);
  const { download, loading: downloadLoading } = useDownload();

  const {
    data,
    isLoading,
    isFetchingNextPage,
    total_count,
  } = useInfiniteScroll<
    WithFormatted<ComplianceDocument>,
    Record<string, unknown>,
    UnifiedSearchResponse
  >({
    section: 'Global Search Compliance Documents',
    fetchFn: ({ offset, limit }) =>
      fetchUnifiedSearch({
        q: trimmedQuery,
        ttype: ['compliance_documents'],
        offset,
        limit,
      }),
    queryKey: [
      ...assetQueryKeys.compliance_documents.globalSearch,
      trimmedQuery,
    ],
    scrollContainerRef,
    useWindowScroll: false,
    pageSize: 20,
    enabled: hasSufficientQuery,
    extractData: (response) =>
      (response.compliance_documents
        ?.hits as WithFormatted<ComplianceDocument>[]) ?? [],
  });

  useEffect(() => {
    if (!hasSufficientQuery || isLoading) return;
    onResultsLoaded?.({
      query: trimmedQuery,
      scope: 'compliance-documents',
      resultCount: total_count ?? data.length,
    });
  }, [
    data.length,
    hasSufficientQuery,
    isLoading,
    onResultsLoaded,
    total_count,
    trimmedQuery,
  ]);

  const handleSelect = useCallback(
    (next: number[]) => {
      const nextSet = new Set(next);
      addMany('compliance_documents', next);
      removeMany(
        'compliance_documents',
        idsFor('compliance_documents').filter((id) => !nextSet.has(id)),
      );
    },
    [addMany, removeMany, idsFor],
  );

  // Keyed on the joined ids, not the array: `extractData` is an inline arrow,
  // so `data` is a fresh reference every render and an identity-keyed effect
  // here would re-render this panel forever.
  const loadedIdsKey = data.map((file) => file.id).join(',');

  useEffect(() => {
    onLoadedFilesChange?.({
      ids: loadedIdsKey ? loadedIdsKey.split(',').map(Number) : [],
      total: total_count,
    });
  }, [loadedIdsKey, total_count, onLoadedFilesChange]);

  const handleFileOpen = (file: { id: number }, index: number) => {
    ga.trackSearchResultClick({
      result_type: 'compliance_document',
      result_id: file.id,
      position: index + 1,
      search_term: trimmedQuery,
    });
  };

  const explorerDocs = useMemo(() => {
    return data.map((doc) => {
      if (!doc.file_path) return doc;
      return {
        ...doc,
        file_path: parseSearchHitPath(doc.file_path, ''),
      };
    });
  }, [data]);

  // The basket spans sections; this tab's Download acts only on the rows it
  // has actually loaded.
  const viewSelected = useMemo(() => {
    const onScreen = new Set(data.map((file) => file.id));
    return selected.filter((id) => onScreen.has(id));
  }, [data, selected]);

  const handleDownloadSelected = () => {
    if (!viewSelected.length) return;
    download({
      category: 'compliance_documents',
      file_ids: viewSelected,
      entity_name: 'Selected compliance documents',
    });
  };

  return (
    <ScrollableTabContent>
      {data.length > 0 ? (
        viewMode === 'grid' ? (
          <>
            <ViewBar>
              <DownloadButton
                variant="contained"
                onClick={handleDownloadSelected}
                disabled={viewSelected.length === 0}
                loading={downloadLoading}
                aria-label="Download selected compliance documents"
              >
                <DownloadIcon />
                <FormattedMessage
                  id="download"
                  defaultMessage="Download"
                />
              </DownloadButton>
              <ModeSwitch mode={viewMode} onChange={setViewMode} />
            </ViewBar>
            <FileGrid
              type="compliance"
              files={data as ComplianceDocument[]}
              selected={selected}
              onSelect={handleSelect}
              onFileOpen={handleFileOpen}
              exactMatchQuery={trimmedQuery}
            />
          </>
        ) : (
          <FileExplorer
            folderStructure={EMPTY_FOLDER_STRUCTURE}
            files={explorerDocs}
            exactMatchQuery={trimmedQuery}
            filesFlatList={undefined}
            loading={false}
            selected={selected}
            onSelect={handleSelect}
            onDownloadClick={handleDownloadSelected}
            showDownload={false}
            currentView="list"
            path={[]}
            onPathChange={() => {}}
            downloadLoading={downloadLoading}
            actions={
              <>
                <DownloadButton
                  variant="contained"
                  onClick={handleDownloadSelected}
                  disabled={viewSelected.length === 0}
                  loading={downloadLoading}
                  aria-label="Download selected compliance documents"
                >
                  <DownloadIcon />
                  <FormattedMessage
                    id="download"
                    defaultMessage="Download"
                  />
                </DownloadButton>
                <ModeSwitch mode={viewMode} onChange={setViewMode} />
              </>
            }
            hasActiveSearchOrFilter
          />
        )
      ) : !isLoading && !isFetchingNextPage ? (
        <NoResultsText>
          <FormattedMessage
            id="noSearchResults"
            defaultMessage="No search results."
          />
        </NoResultsText>
      ) : null}
      {(isLoading || isFetchingNextPage) && (
        <LoaderContainer>
          <CircularProgress />
        </LoaderContainer>
      )}
    </ScrollableTabContent>
  );
};

export default ComplianceDocumentsSearchTabPanel;

const ScrollableTabContent = styled('div')({});

const ViewBar = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: theme.spacing(2),
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const DownloadButton = styled(Button)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  whiteSpace: 'nowrap',
}));

const LoaderContainer = styled('div')({
  width: '100%',
  padding: '40px',
  display: 'flex',
  justifyContent: 'center',
});

const NoResultsText = styled('div')(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(6),
  textAlign: 'center',
  color: theme.palette.textSecondaryDarkGrey,
}));
